import type { ArtworkRequestListItem } from "@hops/sdk/types";
import { useState } from "react";
import { RequestListItem } from "./RequestListItem";

type TabFilter = "mine" | "all";

interface RequestListProps {
  items: ArtworkRequestListItem[];
  activeId?: number | string;
  currentUserId?: number | string | null;
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  onSelect: (item: ArtworkRequestListItem) => void;
  onRefresh?: () => void;
}

export function RequestList({
  items,
  activeId,
  currentUserId,
  isLoading,
  hasNextPage,
  onLoadMore,
  onSelect,
  onRefresh,
}: RequestListProps) {
  const [tab, setTab] = useState<TabFilter>("mine");
  const [search, setSearch] = useState("");

  const filtered = items.filter((item) => {
    // Tab filter
    if (tab === "mine" && currentUserId != null) {
      if (String(item.assignedToUserId) !== String(currentUserId)) return false;
    }

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      return (
        item.requestId?.toLowerCase().includes(q) ||
        item.custOrgName?.toLowerCase().includes(q) ||
        item.productName?.toLowerCase().includes(q) ||
        item.transId?.toLowerCase().includes(q)
      );
    }

    return true;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid var(--panel-border)",
        }}
      >
        {(["mine", "all"] as const).map((t) => (
          <button
            key={t}
            className="hops-btn hops-btn-ghost"
            onClick={() => setTab(t)}
            data-testid={`panel-tab-${t}`}
            style={{
              flex: 1,
              borderRadius: 0,
              borderBottom:
                tab === t
                  ? "2px solid var(--panel-accent)"
                  : "2px solid transparent",
              color:
                tab === t ? "var(--panel-text)" : "var(--panel-text-muted)",
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t === "mine" ? "My Queue" : "All Requests"}
          </button>
        ))}
        {onRefresh && (
          <button
            className="hops-btn hops-btn-ghost hops-btn-sm"
            onClick={onRefresh}
            title="Refresh"
            style={{ flexShrink: 0, padding: "var(--panel-spacing-xs)" }}
          >
            ↻
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: "var(--panel-spacing-sm)" }}>
        <input
          className="hops-input"
          type="text"
          placeholder="Search requests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="panel-search-input"
        />
      </div>

      {/* List */}
      <div
        style={{ flex: 1, overflowY: "auto" }}
        data-testid="panel-request-list"
      >
        {isLoading && items.length === 0 ? (
          <div
            style={{
              padding: "var(--panel-spacing-xl)",
              textAlign: "center",
              color: "var(--panel-text-muted)",
            }}
          >
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              padding: "var(--panel-spacing-xl)",
              textAlign: "center",
              color: "var(--panel-text-muted)",
            }}
          >
            {search
              ? "No matching requests"
              : tab === "mine"
                ? "No requests assigned to you"
                : "No requests"}
          </div>
        ) : (
          <>
            {filtered.map((item) => (
              <RequestListItem
                key={String(item.id)}
                item={item}
                isActive={String(item.id) === String(activeId)}
                onClick={onSelect}
              />
            ))}
            {hasNextPage && (
              <div
                style={{
                  padding: "var(--panel-spacing-sm)",
                  textAlign: "center",
                }}
              >
                <button
                  className="hops-btn hops-btn-ghost hops-btn-sm hops-btn-full"
                  onClick={onLoadMore}
                  disabled={isLoading}
                  data-testid="panel-load-more"
                >
                  {isLoading ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
